import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Heart, QrCode, Users, TrendingUp, Calendar, Copy, X, Printer, Search, Plus, BookUser, Home } from "lucide-react";
import { DirectoryPanel } from "@/pages/CoordinatorDashboard";
import HouseholdsPanel from "@/components/crm/HouseholdsPanel";
import ChurchToolsSwitcher from "@/components/ChurchToolsSwitcher";
import { formatDisplayName } from "@/lib/names";

// Admin dashboard for thedisciple.prayer. Visible to is_admin / is_org_admin /
// is_prayer_admin. Shows stats, recent check-ins, searchable log, manual entry,
// reports (day/weekday/hour breakdowns), plus a "Prayer admins" role manager for
// org admins to grant/revoke the is_prayer_admin flag on their staff.

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "year", label: "This year" },
];

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="p-4 rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid={testid}>
      <div className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>{label}</div>
      <div className="font-serif-display text-3xl mt-1" style={{ color: "var(--ink)" }}>{value}</div>
      {hint && <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{hint}</div>}
    </div>
  );
}

function QrModal({ orgId, onClose }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.get("/prayer/admin/qr-code", { params: { org_id: orgId } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load the QR."));
  }, [orgId]);
  const publicUrl = data ? `${window.location.origin}/prayer/${data.code}` : "";
  const copy = async () => { try { await navigator.clipboard.writeText(publicUrl); setCopied(true); toast.success("Link copied."); setTimeout(() => setCopied(false), 1500); } catch { toast.error("Couldn't copy."); } };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4 print:static" style={{ background: "rgba(14,34,64,0.55)" }} onClick={onClose} data-testid="prayer-admin-qr-overlay">
      <style>{`@media print { body * { visibility: hidden; } .qr-print, .qr-print * { visibility: visible; } .qr-print { position: absolute; left: 0; top: 0; width: 100%; } .qr-no-print { display: none !important; } }`}</style>
      <div className="w-full max-w-md rounded-md qr-print" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-start justify-between qr-no-print">
            <div>
              <div className="overline mb-1" style={{ color: "var(--terracotta)", fontSize: "0.6rem" }}>Prayer QR</div>
              <h3 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>{data?.org_name || "\u2026"}</h3>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]" style={{ color: "var(--muted)" }}><X size={18} /></button>
          </div>
          <div className="mt-6 flex flex-col items-center text-center">
            <div className="p-6 rounded" style={{ background: "#FFFFFF" }}>
              {publicUrl ? <QRCodeSVG value={publicUrl} size={220} level="M" /> : <div style={{ width: 220, height: 220 }} />}
            </div>
            <div className="mt-4 font-serif-display text-xl" style={{ color: "var(--ink)" }}>Scan to check in for prayer</div>
            <div className="mt-1 text-sm max-w-xs" style={{ color: "var(--muted)" }}>
              Post this in the prayer room. Different from your attendance QR — this one records prayer visits any day, any time.
            </div>
            <div className="mt-4 text-[0.65rem] px-3 py-1.5 rounded font-mono break-all" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }}>{publicUrl || "\u2026"}</div>
          </div>
          <div className="mt-6 flex gap-2 flex-wrap qr-no-print">
            <button onClick={copy} disabled={!publicUrl} className="btn-ghost"><Copy size={13} /> {copied ? "Copied" : "Copy link"}</button>
            <button onClick={() => window.print()} disabled={!publicUrl} className="btn-primary"><Printer size={13} /> Print poster</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManualEntryModal({ orgId, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [when, setWhen] = useState(new Date().toISOString().slice(0, 16));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/coord/guests", { params: { org_id: orgId } });
        const ql = q.toLowerCase();
        const list = (r.data || []).filter((g) => `${g.first_name} ${g.last_name}`.toLowerCase().includes(ql) || (g.phone || "").includes(q)).slice(0, 15);
        setMatches(list);
      } catch { setMatches([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, orgId]);

  const save = async () => {
    if (!picked) { toast.error("Pick a person first."); return; }
    setBusy(true);
    try {
      await api.post("/prayer/admin/manual", { directory_person_id: picked.id, check_in_at: when }, { params: { org_id: orgId } });
      toast.success("Recorded.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't record.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-10 px-4" style={{ background: "rgba(14,34,64,0.55)" }} onClick={onClose} data-testid="prayer-manual-overlay">
      <div className="w-full max-w-md rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <h3 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>Record a prayer visit</h3>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-[var(--surface-hover)]"><X size={18} /></button>
          </div>
          <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>Person</label>
          {picked ? (
            <div className="flex items-center justify-between p-3 rounded" style={{ background: "var(--surface-hover)" }}>
              <span style={{ color: "var(--ink)" }}>{formatDisplayName(picked)}</span>
              <button onClick={() => setPicked(null)} className="text-xs underline" style={{ color: "var(--terracotta)" }}>Change</button>
            </div>
          ) : (
            <>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={"Search directory\u2026"} className="w-full px-3 py-2 text-sm" data-testid="prayer-manual-search" />
              {matches.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto" style={{ border: "1px solid var(--border-subtle)" }}>
                  {matches.map((m) => (
                    <button key={m.id} onClick={() => { setPicked(m); setQ(""); setMatches([]); }} className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--surface-hover)]" data-testid={`prayer-manual-pick-${m.id}`}>
                      {formatDisplayName(m)}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          <label className="block text-[0.6rem] uppercase tracking-[0.18em] mt-4 mb-1" style={{ color: "var(--muted)" }}>Date &amp; time</label>
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="w-full px-3 py-2 text-sm" data-testid="prayer-manual-when" />
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button onClick={save} disabled={!picked || busy} className="btn-primary" data-testid="prayer-manual-save">{busy ? "Saving\u2026" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StaffRoles({ orgId, canManage }) {
  const [staff, setStaff] = useState([]);
  const load = () => {
    api.get("/prayer/admin/staff", { params: { org_id: orgId } }).then((r) => setStaff(r.data.staff || [])).catch(() => {});
  };
  useEffect(load, [orgId]);
  const toggle = async (u) => {
    try {
      await api.put(`/prayer/admin/staff/${u.id}`, { enabled: !u.is_prayer_admin }, { params: { org_id: orgId } });
      toast.success(`${u.name} ${u.is_prayer_admin ? "removed from" : "added as"} prayer admin.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    }
  };
  if (!canManage) return null;
  return (
    <div className="p-4 rounded-md" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="prayer-staff">
      <h3 className="font-serif-display text-lg mb-2" style={{ color: "var(--ink)" }}>Prayer admins</h3>
      <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>Grant staff access to the prayer dashboard, log, and manual entry.</p>
      {staff.length === 0 ? (
        <p className="text-xs italic" style={{ color: "var(--muted)" }}>No staff yet.</p>
      ) : (
        <div className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
          {staff.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2 text-sm" style={{ borderColor: "var(--border-subtle)" }}>
              <div>
                <div style={{ color: "var(--ink)" }}>{u.name}</div>
                <div className="text-xs" style={{ color: "var(--muted)" }}>{u.email}{u.is_org_admin ? " · Org admin" : ""}{u.is_coordinator ? " · Coordinator" : ""}</div>
              </div>
              <button onClick={() => toggle(u)} className="text-xs px-3 py-1 rounded-full border" style={{ background: u.is_prayer_admin ? "var(--terracotta)" : "transparent", color: u.is_prayer_admin ? "#FFF" : "var(--ink-2)", borderColor: u.is_prayer_admin ? "var(--terracotta)" : "var(--border-strong)" }} data-testid={`prayer-toggle-${u.id}`}>
                {u.is_prayer_admin ? "Prayer admin" : "Grant"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PrayerAdmin() {
  const { user, loading } = useAuth();
  const [orgId, setOrgId] = useState(user?.org_id || "");
  const canView = user && (user.is_admin || user.is_org_admin || user.is_prayer_admin);
  const canManageStaff = user && (user.is_admin || user.is_org_admin);

  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [range, setRange] = useState("month");
  const [log, setLog] = useState([]);
  const [logQ, setLogQ] = useState("");
  const [reports, setReports] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [view, setView] = useState("overview"); // "overview" | "directory" | "households"

  useEffect(() => {
    if (user?.is_admin) {
      api.get("/admin/orgs").then((r) => { setOrgs(r.data || []); if (!orgId && r.data?.[0]) setOrgId(r.data[0].id); }).catch(() => {});
    }
  }, [user, orgId]);

  const loadAll = () => {
    if (!orgId) return;
    api.get("/prayer/admin/stats", { params: { org_id: orgId } }).then((r) => setStats(r.data)).catch(() => {});
    api.get("/prayer/admin/recent", { params: { org_id: orgId } }).then((r) => setRecent(r.data.recent || [])).catch(() => {});
    api.get("/prayer/admin/log", { params: { org_id: orgId, range } }).then((r) => setLog(r.data.items || [])).catch(() => {});
    api.get("/prayer/admin/reports", { params: { org_id: orgId, range } }).then((r) => setReports(r.data)).catch(() => {});
  };
  useEffect(loadAll, [orgId, range]);

  const filteredLog = useMemo(() => {
    if (!logQ.trim()) return log;
    const ql = logQ.toLowerCase();
    return log.filter((r) => (r.person_display_name || "").toLowerCase().includes(ql));
  }, [log, logQ]);

  const topWeekdays = useMemo(() => {
    if (!reports?.by_weekday) return [];
    const names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return Object.entries(reports.by_weekday).map(([k, v]) => ({ label: names[Number(k)] || k, count: v })).sort((a, b) => b.count - a.count);
  }, [reports]);
  const topHours = useMemo(() => {
    if (!reports?.by_hour) return [];
    return Object.entries(reports.by_hour).map(([k, v]) => ({ label: `${(Number(k) % 12) || 12}${Number(k) < 12 ? "am" : "pm"}`, count: v })).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [reports]);

  if (loading) return null;
  if (!user) return <Navigate to="/login?next=/prayer-admin" replace />;
  if (!canView) return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="prayer-admin">
      <div className="mb-6">
        <ChurchToolsSwitcher />
      </div>
      <header className="mb-8">
        <div className="overline mb-2" style={{ color: "var(--terracotta)", fontSize: "0.6rem" }}>thedisciple.prayer</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <h1 className="font-serif-display text-4xl sm:text-5xl" style={{ color: "var(--ink)" }}>Prayer <span style={{ color: "var(--terracotta)", fontStyle: "italic" }}>admin</span></h1>
          <div className="flex gap-2 flex-wrap max-w-full">
            {orgs.length > 1 && (
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)} className="text-sm px-3 py-2 max-w-full" data-testid="prayer-admin-org">
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
            <button onClick={() => setQrOpen(true)} className="btn-ghost" data-testid="prayer-admin-show-qr"><QrCode size={14} /> Show QR</button>
            <button onClick={() => setManualOpen(true)} className="btn-primary" data-testid="prayer-admin-manual"><Plus size={14} /> Record visit</button>
          </div>
        </div>
      </header>

      <div className="flex items-center gap-1 sm:gap-2 mb-6 border-b overflow-x-auto" style={{ borderColor: "var(--border-subtle)" }} data-testid="prayer-view-tabs">
        <button
          onClick={() => setView("overview")}
          className="px-3 sm:px-4 py-2 text-sm shrink-0"
          style={{ color: view === "overview" ? "var(--ink)" : "var(--muted)", borderBottom: `2px solid ${view === "overview" ? "var(--terracotta)" : "transparent"}` }}
          data-testid="prayer-view-overview"
        >
          <span className="inline-flex items-center gap-1.5"><TrendingUp size={13} /> Overview</span>
        </button>
        <button
          onClick={() => setView("directory")}
          className="px-3 sm:px-4 py-2 text-sm shrink-0"
          style={{ color: view === "directory" ? "var(--ink)" : "var(--muted)", borderBottom: `2px solid ${view === "directory" ? "var(--terracotta)" : "transparent"}` }}
          data-testid="prayer-view-directory"
        >
          <span className="inline-flex items-center gap-1.5"><BookUser size={13} /> Directory</span>
        </button>
        <button
          onClick={() => setView("households")}
          className="px-3 sm:px-4 py-2 text-sm shrink-0"
          style={{ color: view === "households" ? "var(--ink)" : "var(--muted)", borderBottom: `2px solid ${view === "households" ? "var(--terracotta)" : "transparent"}` }}
          data-testid="prayer-view-households"
        >
          <span className="inline-flex items-center gap-1.5"><Home size={13} /> Households</span>
        </button>
      </div>

      {view === "directory" ? (
        <DirectoryPanel orgId={orgId} />
      ) : view === "households" ? (
        <HouseholdsPanel orgId={orgId} />
      ) : (
        <>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <StatCard label="Today" value={stats.today.visits} hint={`${stats.today.unique} unique`} testid="prayer-stat-today" />
          <StatCard label="This week" value={stats.week.visits} hint={`${stats.week.unique} unique`} testid="prayer-stat-week" />
          <StatCard label="This month" value={stats.month.visits} hint={`${stats.month.unique} unique`} testid="prayer-stat-month" />
          <StatCard label="Directory" value={stats.active_directory} hint="active members" testid="prayer-stat-directory" />
        </div>
      )}

      {/* Recent */}
      <section className="mb-8">
        <h2 className="font-serif-display text-2xl mb-3" style={{ color: "var(--ink)" }}>Recent check-ins</h2>
        {recent.length === 0 ? (
          <p className="text-sm italic" style={{ color: "var(--muted)" }}>Nothing yet.</p>
        ) : (
          <div className="divide-y" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)", borderRadius: "6px" }} data-testid="prayer-recent-list">
            {recent.map((r) => (
              <div key={r.id} className="px-4 py-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span style={{ color: "var(--ink)" }}>{r.person_display_name}</span>
                  <span className="text-[0.7rem]" style={{ color: "var(--muted)" }}>{fmt(r.check_in_at)} &middot; {r.method}</span>
                </div>
                {r.prayer_request && (
                  <p className="text-xs mt-1 pl-2 border-l-2 leading-snug italic" style={{ color: "var(--ink-2)", borderColor: "var(--terracotta)" }}>
                    &ldquo;{r.prayer_request}&rdquo;
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Log with filters */}
      <section className="mb-8">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>Prayer log</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {RANGES.map((r) => (
              <button key={r.key} onClick={() => setRange(r.key)} className="text-xs px-3 py-1 rounded-full border" style={{ background: range === r.key ? "var(--ink)" : "transparent", color: range === r.key ? "#FFF" : "var(--ink-2)", borderColor: range === r.key ? "var(--ink)" : "var(--border-strong)" }} data-testid={`prayer-range-${r.key}`}>
                {r.label}
              </button>
            ))}
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
              <input value={logQ} onChange={(e) => setLogQ(e.target.value)} placeholder={"Name\u2026"} className="pl-7 pr-3 py-1.5 text-xs" data-testid="prayer-log-search" />
            </div>
          </div>
        </div>
        {filteredLog.length === 0 ? (
          <p className="text-sm italic" style={{ color: "var(--muted)" }}>No entries in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="prayer-log-table">
              <thead className="text-left text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
                <tr><th className="py-2">Member</th><th>Date &amp; time</th><th>Method</th></tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
                {filteredLog.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2" style={{ color: "var(--ink)" }}>
                      <div>{r.person_display_name}{r.manually_added ? <span className="ml-2 text-[0.6rem] px-1.5 py-0.5 rounded" style={{ background: "var(--surface-hover)", color: "var(--muted)" }}>manual</span> : null}</div>
                      {r.prayer_request && (
                        <div className="text-xs mt-0.5 pl-2 border-l-2 italic max-w-md" style={{ color: "var(--ink-2)", borderColor: "var(--terracotta)" }}>
                          &ldquo;{r.prayer_request}&rdquo;
                        </div>
                      )}
                    </td>
                    <td style={{ color: "var(--ink-2)" }}>{fmt(r.check_in_at)}</td>
                    <td className="text-xs" style={{ color: "var(--muted)" }}>{r.method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Reports */}
      {reports && (
        <section className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="p-4 rounded-md lg:col-span-1" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="prayer-report-participation">
            <div className="flex items-center gap-2 mb-2"><Users size={13} style={{ color: "var(--muted)" }} /><span className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Participation</span></div>
            <div className="font-serif-display text-3xl" style={{ color: "var(--ink)" }}>{reports.participation_pct}%</div>
            <div className="text-xs" style={{ color: "var(--muted)" }}>{reports.unique_people} of {reports.active_directory} directory members</div>
          </div>
          <div className="p-4 rounded-md lg:col-span-1" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="prayer-report-days">
            <div className="flex items-center gap-2 mb-2"><Calendar size={13} style={{ color: "var(--muted)" }} /><span className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Most common prayer days</span></div>
            {topWeekdays.length === 0 ? <p className="text-xs italic" style={{ color: "var(--muted)" }}>No data.</p> : (
              <ul className="text-sm space-y-1">
                {topWeekdays.map((d) => <li key={d.label} className="flex justify-between" style={{ color: "var(--ink-2)" }}><span>{d.label}</span><span>{d.count}</span></li>)}
              </ul>
            )}
          </div>
          <div className="p-4 rounded-md lg:col-span-1" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="prayer-report-hours">
            <div className="flex items-center gap-2 mb-2"><TrendingUp size={13} style={{ color: "var(--muted)" }} /><span className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Most common prayer times</span></div>
            {topHours.length === 0 ? <p className="text-xs italic" style={{ color: "var(--muted)" }}>No data.</p> : (
              <ul className="text-sm space-y-1">
                {topHours.map((h) => <li key={h.label} className="flex justify-between" style={{ color: "var(--ink-2)" }}><span>{h.label}</span><span>{h.count}</span></li>)}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* Staff */}
      <section>
        <StaffRoles orgId={orgId} canManage={canManageStaff} />
      </section>
      </>
      )}

      {qrOpen && <QrModal orgId={orgId} onClose={() => setQrOpen(false)} />}
      {manualOpen && <ManualEntryModal orgId={orgId} onClose={() => setManualOpen(false)} onSaved={loadAll} />}
    </div>
  );
}
